<script setup lang="ts">
import { dollars } from '@nuts/auto-delivery-sdk/dist/utils/money';
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';

import AppliedDiscountsPopper from '@/components/base/AppliedDiscountsPopper.vue';
import ChevronIcon from '@/components/base/assets/ChevronIcon.vue';
import CouponIcon from '@/components/base/assets/CouponIcon.vue';
import InfoIcon from '@/components/base/assets/InfoIcon.vue';
import Collapse from '@/components/base/layout/Collapse.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import Header4 from '@/components/base/typography/Header4.vue';
import Header5 from '@/components/base/typography/Header5.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import UnstyledButton from '@/components/base/UnstyledButton.vue';
import DiscountCode from '@/components/cart/DiscountCode.vue';
import SubscribeForm from '@/components/cart/SubscribeForm.vue';
import { useDiscount } from '@/composables/useDiscount';
import { usePayment } from '@/composables/usePayment';
import money from '@/filters/money';
import { adaptSavings } from '@/utils/cart';
import { Money } from '@/utils/money';

defineProps<{
  combinedSavings?: Money;
  containsAutoDelivery?: boolean;
  containsProductDiscounts?: boolean;
  freeShipping?: boolean;
  subTotalPrice: Money;
  totalDiscountedAmount?: Money;
  totalPrice: Money;
}>();

const emit = defineEmits<{
  'refresh-content': [];
}>();

const store = useStore();

const { appliedDiscountCodes, availableDiscountCodes, pendingDiscountCodes } = useDiscount(store);
const loading = ref(false);
const { totalSavings } = usePayment(store, ref(false));

const savingsBreakdown = computed(() =>
  totalSavings.value ? adaptSavings(totalSavings.value) : [],
);

watch(loading, (isLoading, wasLoading) => {
  if (wasLoading && !isLoading) emit('refresh-content');
});

const showPromos = ref(
  !!(pendingDiscountCodes.value.length || availableDiscountCodes.value.length),
);

const showNewsletterSignup = ref(false);
</script>

<template>
  <div :class="{ 'opacity-50 pointer-events-none': loading }" data-test="order-summary">
    <Header4 data-test="summary-header">Order Summary</Header4>
    <dl class="flex justify-between w-full mt-3">
      <BaseBodyText tag="dt">Subtotal</BaseBodyText>
      <BaseBodyText tag="dd" data-test="subtotal" class="text-right">
        {{ money(subTotalPrice) }}
      </BaseBodyText>
    </dl>
    <transition name="fade">
      <dl v-if="freeShipping" class="flex justify-between w-full mt-1.5">
        <BaseBodyText tag="dt">Shipping</BaseBodyText>
        <BaseBodyText tag="dd" data-test="shipping" class="text-right">FREE</BaseBodyText>
      </dl>
    </transition>
    <transition name="fade">
      <dl
        v-if="totalDiscountedAmount && dollars(totalDiscountedAmount)"
        class="flex justify-between w-full mt-1.5"
      >
        <BaseBodyText tag="dt">
          {{ appliedDiscountCodes.length ? 'Promo discount' : 'Discount' }}
        </BaseBodyText>
        <BaseBodyText class="text-right" data-test="discount-amount" tag="dd">
          {{ money(totalDiscountedAmount) }}
        </BaseBodyText>
      </dl>
    </transition>
    <div class="flex flex-row flex-wrap gap-1 pt-3 mt-3 border-t border-solid border-neutral-200">
      <UnstyledButton
        :aria-expanded="showPromos"
        class="flex items-center justify-between w-full"
        data-test="promo-section"
        @click="showPromos = !showPromos"
      >
        <BaseBodyText class="font-semibold">Promos</BaseBodyText>
        <ChevronIcon
          class="object-contain shrink-0"
          :direction="showPromos ? 'up' : 'down'"
          :size="[16, 20]"
        />
      </UnstyledButton>
      <Collapse class="flex flex-col w-full gap-1 md:gap-2" :expanded="showPromos">
        <BaseBodyText
          v-if="!pendingDiscountCodes.length && !availableDiscountCodes.length"
          class="w-full text-center text-nuts-neutral-800"
          data-test="promo-text"
        >
          Check your email for any ongoing promos or
          <UnstyledButton class="underline color-inherit" @click="showNewsletterSignup = true">
            sign up
          </UnstyledButton>
          for our newsletters to get exclusive promotions
        </BaseBodyText>
        <SubscribeForm v-if="showNewsletterSignup" class="w-full mt-3" />
        <DiscountCode
          v-for="discountCode in pendingDiscountCodes"
          v-model:loading="loading"
          class="w-full"
          :discountCode
          :key="discountCode.label"
          pendingCodes
          :subTotalPrice
        />
        <DiscountCode
          v-for="discountCode in availableDiscountCodes"
          v-model:loading="loading"
          class="w-full"
          :discountCode
          :key="discountCode.label"
          :subTotalPrice
        />
      </Collapse>
    </div>
    <Header5 class="flex justify-between pt-3 mt-3 border-t border-solid border-neutral-200">
      <span>Estimated Total</span>
      <span class="text-right" data-test="estimated-total">
        {{ money(totalPrice) }}
      </span>
    </Header5>
    <transition name="fade">
      <SmallBodyText
        v-if="totalSavings"
        class="flex items-center mt-1 font-semibold uppercase text-nuts-lime-800"
      >
        <CouponIcon class="mr-1" :size="20" />
        <span class="flex items-center" data-test="total-savings">Total Savings</span>
        <span data-test="estimated-total">&nbsp;{{ money(totalSavings.value) }}</span>
        <AppliedDiscountsPopper data-test="discount-popover-icon" :discounts="savingsBreakdown">
          <InfoIcon alt="Applied Discounts Info Icon" class="text-neutral-500" :size="16" />
        </AppliedDiscountsPopper>
      </SmallBodyText>
    </transition>
    <SmallBodyText class="pt-1.5 text-neutral-500">
      Taxes and final shipping cost calculated at checkout
    </SmallBodyText>
  </div>
</template>
